/**
 * Malformed input, as opposed to a wrong answer.
 *
 * The scorer throws rather than quietly scoring zero, because the two mistakes have very
 * different costs: a 400 with a precise message is a bug someone fixes, and a silent zero is a
 * real student losing real marks. `/v1/score` maps this to 400 and never to 500.
 *
 * **`code` and `path` exist so a caller can branch and an author can navigate.** A message is
 * for a person; a host deciding whether to retry, reject the scheme, or page someone cannot
 * parse prose, and `path` is what turns "something is wrong" into "look at this item". The
 * message stays imperative — it is the only part a human reads — but it is no longer the only
 * thing carried.
 */

/**
 * The closed set. Every throw in this package uses one of these, and adding a case means
 * adding a value here — which is the point: a caller can exhaustively handle them.
 *
 * Kebab-case to match every other enumerated value in the contract (`ITEM_TYPES`,
 * `CREDIT_RULES`, `PAIRING_RULES`), rather than the SCREAMING_SNAKE convention this
 * borrows the idea from.
 */
export const SCORING_ERROR_CODES = [
  /** The scheme declares an item type this scorer has no case for. */
  'unknown-item-type',
  /** A response is the wrong shape for the item it answers. */
  'invalid-response',
  /** A response was supplied for an item the scheme does not grade. */
  'unknown-item',
  /**
   * The scheme is a shape this scorer cannot grade — a field its own `type` and `credit` require
   * is missing or contradictory. Distinct from `unknown-item-type`, which is a type this version
   * has never heard of; this is a type it knows, described in a way that cannot be marked.
   *
   * Thrown rather than defaulted, because every default available would silently grade the item
   * under a rule the author did not choose, and the arithmetic would look plausible.
   */
  'invalid-scheme',
] as const;

export type ScoringErrorCode = (typeof SCORING_ERROR_CODES)[number];

export class ScoringError extends Error {
  readonly code: ScoringErrorCode;
  /** Where in the input, as a dotted path — `items.q1.type`, `responses.q3`. */
  readonly path: string | undefined;

  constructor(code: ScoringErrorCode, message: string, path?: string) {
    super(message);
    this.name = 'ScoringError';
    this.code = code;
    this.path = path;
  }
}
